import { computed, onUnmounted, ref } from 'vue'
import { useQueryClient, useRoute } from '@fusapp-work/shell'
import { useConsolePrincipal } from '../../features/organization/personnel/useConsolePrincipal'
import { useCurrentSession } from '../../composables/useCurrentSession'
import { organizationSchema } from '../../features/organization/shared/organizationApi'
import { companySchema } from '../../features/organization/companies/domain/company'
import { rememberedOrganization } from '../../features/organization/shared/organizationPreference'
import { navigationSummarySchema } from './navigationSummary'
/** Read validated principal-scoped facts from the shared query cache. */
export function useConsoleCachedFacts() {
  const { state } = useCurrentSession(); const client = useQueryClient(); const principal = useConsolePrincipal(); const route = useRoute(); const revision = ref(0)
  const unsubscribe = client.getQueryCache().subscribe(() => { revision.value++ }); onUnmounted(unsubscribe)
  return computed(() => {
    void revision.value
    const counts: Partial<Record<'companies' | 'users' | 'roles' | 'audit' | 'welcome' | 'channels' | 'templates' | 'transactions', number>> = {}
    let accent: string | undefined
    if (!principal.value || !state.session?.roles.includes('System_Admin')) return { counts, accent, organizationName: undefined, organizationId: undefined }
    const explicitOrgId = typeof route.query.orgId === 'string' ? route.query.orgId : ''
    const scope = client.getQueryState(['console', principal.value, 'organizations'])
    const organizations = organizationSchema.array().safeParse(scope?.status === 'success' ? scope.data : undefined)
    const selectedOrganization = organizations.success && route.name ? (Object.hasOwn(route.query, 'orgId')
      ? organizations.data.find(row => row.id === explicitOrgId)
      : organizations.data.find(row => row.id === rememberedOrganization(principal.value)) ??
        (organizations.data.length === 1 ? organizations.data[0] : undefined)) : undefined
    const orgId = selectedOrganization?.id ?? ''
    const organizationName = selectedOrganization?.name
    const companyState = client.getQueryState(['console', principal.value, 'companies', orgId])
    const companies = companySchema.array().safeParse(companyState?.status === 'success' ? companyState.data : undefined)
    if (selectedOrganization && companies.success && companies.data.every(row => row.organizationId === orgId)) {
      const selected = companies.data.find(row => row.id === route.query.companyId)
      if (route.name === 'organization-companies' && selected && /^#[\da-f]{6}$/i.test(selected.branding.accentColor)) accent = selected.branding.accentColor
    }
    const summaryState = client.getQueryState(['console', principal.value, 'navigation-summary', orgId])
    const summary = navigationSummarySchema.safeParse(summaryState?.status === 'success' ? summaryState.data : undefined)
    if (selectedOrganization && summary.success && summary.data.organization.id === orgId) {
      const { organization, cosmos } = summary.data
      if (organization.companies !== null) counts.companies = organization.companies
      if (organization.audit !== null) counts.audit = organization.audit
      if (cosmos.users !== null) counts.users = cosmos.users
      if (cosmos.roles !== null) counts.roles = cosmos.roles
      if (cosmos.managedChannels !== null) counts.channels = cosmos.managedChannels
      if (cosmos.templates !== null) counts.templates = cosmos.templates
      if (cosmos.permanentErrorTransactions !== null) counts.transactions = cosmos.permanentErrorTransactions
      if (cosmos.draftWelcomeScreens !== null) counts.welcome = cosmos.draftWelcomeScreens
    }
    return { counts, accent, organizationName, organizationId: selectedOrganization?.id }
  })
}
